import { StrictMode } from "react";
import { createRoot, type Root } from "react-dom/client";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { createMsalConfig } from "./auth/authConfig";
import { handleRedirectWithCodeFallback, shouldDeferEmbeddedAuthResponse } from "./auth/authRedirect";
import { initializeTeamsAuth } from "./auth/teamsAuth";
import { setAuthInstances } from "./auth/authInstances";
import { Buffer } from 'buffer';

import App from "@/App";

import "./index.css";
import "katex/dist/katex.min.css";

declare global {
  interface Window {
    Buffer: typeof Buffer;
  }
}
window.Buffer = Buffer;

// Reuse a single React root across Hot Module Replacement. Stored on window so
// it survives module re-evaluation. Without this, an HMR-triggered re-run could
// create a second root on #root, remounting the app and wiping React state
// (which also re-triggers the initial data fetches).
function renderApp(msalInstance: PublicClientApplication) {
  const container = document.getElementById('root')!;
  let root: Root | undefined = (window as any).__appRoot;
  if (!root) {
    root = createRoot(container);
    (window as any).__appRoot = root;
  }
  root.render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </StrictMode>,
  );
}

// Bootstrap runs inside an async function (no top-level await, which would make
// this an async module).
async function bootstrap() {
  // Silent MSAL requests use a hidden iframe; leave its response hash for the parent monitor.
  if (shouldDeferEmbeddedAuthResponse(window.parent !== window, window.location.hash)) return;

  // Initialize Teams SDK first (before MSAL)
  const teamsAuth = await initializeTeamsAuth();

  // Fetch base settings to get MSAL config
  const settingsResponse = await fetch("/api/base_settings");
  const settings = await settingsResponse.json();
  const authSettings = {
    domain: settings.domain,
    azure_tenant_id: settings.azure_tenant_id,
    azure_client_id: settings.azure_client_id
  };

  const msalInstance = new PublicClientApplication(createMsalConfig(authSettings));

  // Only initialize MSAL if not in Teams environment
  if (!teamsAuth.isInTeams) {
    await msalInstance.initialize();
    // Handle redirect immediately to prevent race conditions with API calls
    const canRenderAfterRedirect = await handleRedirectWithCodeFallback(
      () => msalInstance.handleRedirectPromise(),
      window.location,
      (url) => window.location.replace(url),
    );
    if (!canRenderAfterRedirect) return;
  }

  // Publish singletons for api.ts / Root.tsx (which read them at call-time).
  setAuthInstances(teamsAuth, authSettings, msalInstance);

  renderApp(msalInstance);
}

bootstrap();
